import { useState, useEffect } from 'react';
import { BusinessDigitalExposure, BusinessFilingHistory, BusinessHostileWatch, BusinessOwnershipControl, BusinessRelatedCompanies, BusinessSignoffPanel, BusinessSynthesisPanel, BusinessUltimateOwners, CONFIG, EntityGraphPanel, HelpTip, Loading, PANEL_HELP, ReorderableChildren, SubscriptionPanel, api, generateReport, useToast } from './_barrel.js';


        function BusinessDetailView({ token, businessId, setView, setSelectedSubject, openDialog }) {
            const [biz, setBiz] = useState(null);
            const [links, setLinks] = useState(null);
            const [score, setScore] = useState(null);
            const [generating, setGenerating] = useState(false);
            const [officers, setOfficers] = useState(null);
            const [officersError, setOfficersError] = useState(null);
            const [loadingOfficers, setLoadingOfficers] = useState(false);
            const [refreshing, setRefreshing] = useState(false);
            const [error, setError] = useState(null);
            const [refresh, setRefresh] = useState(0);
            const [dismissedSuggestions, setDismissedSuggestions] = useState(() => new Set());
            const [confirmingSuggestion, setConfirmingSuggestion] = useState(null);
            const toast = useToast();

            useEffect(() => {
                (async () => {
                    try {
                        const [b, l, s] = await Promise.all([
                            api(`/api/v1/businesses/${businessId}`, {}, token),
                            api(`/api/v1/businesses/${businessId}/links`, {}, token),
                            api(`/api/v1/businesses/${businessId}/score`, {}, token).catch(() => null),
                        ]);
                        setBiz(b); setLinks(l); setScore(s);
                    } catch (e) { setError(e.message); }
                })();
            }, [token, businessId, refresh]);

            const reload = () => setRefresh(r => r+1);

            const generateReport = async (mode = 'briefing') => {
                setGenerating(true);
                try {
                    // Direct download via authenticated fetch — the endpoint returns PDF bytes
                    const resp = await fetch(`${CONFIG.apiBase}/api/v1/businesses/${businessId}/report?mode=${mode}`, {
                        headers: { 'Authorization': `Bearer ${token}` },
                    });
                    if (!resp.ok) {
                        const txt = await resp.text();
                        throw new Error(`Report generation failed: ${resp.status} ${txt.slice(0, 200)}`);
                    }
                    const blob = await resp.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `veil_business_${mode}_${(biz?.display_name || biz?.legal_name || 'report').replace(/[^a-z0-9]+/gi, '_').toLowerCase()}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                    toast(mode === 'threat' ? 'Threat assessment generated' : 'Briefing generated');
                } catch (e) { setError(e.message); }
                finally { setGenerating(false); }
            };

            const loadOfficers = async () => {
                setLoadingOfficers(true);
                setOfficersError(null);
                try {
                    const r = await api(`/api/v1/businesses/${businessId}/ch-officers`, {}, token);
                    setOfficers(r);
                } catch (e) { setOfficersError(e.message); }
                finally { setLoadingOfficers(false); }
            };

            const refreshCH = async () => {
                if (!confirm('Refresh Companies House snapshot for this business?')) return;
                setRefreshing(true);
                try {
                    await api(`/api/v1/businesses/${businessId}/refresh`, { method: 'POST' }, token);
                    toast('Companies House snapshot refreshed');
                    reload();
                } catch (e) { setError(e.message); }
                finally { setRefreshing(false); }
            };

            const linkExistingSubject = async (officer, subjectId) => {
                try {
                    await api(`/api/v1/businesses/${businessId}/links`, {
                        method: 'POST',
                        body: JSON.stringify({
                            subject_id: subjectId,
                            link_type: (officer.role || 'director').toLowerCase().replace(/[^a-z]/g, '_').slice(0, 64),
                            ch_officer_id: officer.officer_id,
                            appointed_at: officer.appointed_on,
                            resigned_at: officer.resigned_on,
                            is_current: officer.is_current,
                        }),
                    }, token);
                    toast(`Linked ${officer.name}`);
                    await loadOfficers();
                    reload();
                } catch (e) { setError(e.message); }
            };

            const confirmSuggestion = async (officer) => {
                setConfirmingSuggestion(officer.officer_id || officer.name);
                try {
                    await linkExistingSubject(officer, officer.suggested_subject_id);
                } finally {
                    setConfirmingSuggestion(null);
                }
            };

            const dismissSuggestion = (officer) => {
                setDismissedSuggestions(prev => new Set(prev).add(officer.officer_id || officer.name));
            };

            const removeLink = async (link) => {
                if (!confirm(`Remove link to ${link.subject_display_name || link.subject_full_name}?`)) return;
                try {
                    await api(`/api/v1/businesses/${businessId}/links/${link.id}`, { method: 'DELETE' }, token);
                    toast('Link removed');
                    await loadOfficers();
                    reload();
                } catch (e) { setError(e.message); }
            };

            if (error) return <div className="max-w-5xl mx-auto px-6 py-8 text-[var(--red)]">{error}</div>;
            if (!biz || !links) return <Loading />;

            const refreshedAge = biz.ch_last_refreshed_at
                ? Math.floor((Date.now() - new Date(biz.ch_last_refreshed_at)) / (1000*60*60*24))
                : null;

            // UK/US-International coverage badge — Companies House (full record:
            // officers, filings, charges, financials) vs GLEIF-only (name/address/
            // legal-status, nothing else). A UK record has a real CH company
            // number AND has actually been fetched from CH (ch_last_refreshed_at
            // set by /from-ch or /refresh) — jurisdiction alone can't be trusted
            // since manual entries can claim "GB" without a real CH pull.
            const hasFullCHRecord = biz.jurisdiction === 'GB' && !!biz.company_number && !!biz.ch_last_refreshed_at;
            const coverageBadge = hasFullCHRecord
                ? { label: 'UK — full Companies House record', color: 'var(--green)' }
                : { label: 'US/International — name & status only, no officers, filings or financials available', color: 'var(--amber)' };

            const unlinkedOfficers = (officers || []).filter(o => !o.already_linked);
            const suggestedOfficers = unlinkedOfficers.filter(
                o => o.suggested_subject_id && !dismissedSuggestions.has(o.officer_id || o.name)
            );

            return (
                <div className="max-w-7xl mx-auto px-6 py-10">
                    <a className="text-xs font-mono uppercase tracking-widest text-muted cursor-pointer hover:text-[var(--gold)] inline-block mb-6"
                       onClick={() => setView('businesses')}>← Businesses</a>

                    <div className="flex justify-between items-start mb-8">
                        <div>
                            <div className="font-mono text-xs uppercase tracking-widest text-muted mb-2">Business</div>
                            <div className="flex items-center gap-3 flex-wrap mb-2">
                                <h2 className="font-display text-5xl gold" style={{marginBottom: 0}}>{biz.display_name || biz.legal_name}</h2>
                                <span className="font-mono text-xs uppercase tracking-widest px-3 py-1" style={{
                                    color: coverageBadge.color, border: `1px solid ${coverageBadge.color}`, borderRadius: '4px',
                                }}>
                                    {coverageBadge.label}
                                </span>
                            </div>
                            <div className="text-muted font-mono text-sm">
                                {biz.legal_name}
                                {biz.company_number && ` · ${biz.company_number}`}
                                {' · '}{biz.jurisdiction}
                            </div>
                            {biz.client_tag && (
                                <div className="text-sm font-mono mt-2" style={{color:'var(--gold)'}}>
                                    {biz.client_tag}
                                </div>
                            )}
                        </div>
                        <div className="flex gap-3">
                            <button className="btn-gold" onClick={() => generateReport('briefing')} disabled={generating} title="Commercial briefing: who they are, are they legit and sound, who runs it, key risks">
                                {generating ? 'Generating…' : '📋 Briefing PDF'}
                            </button>
                            <button className="btn-gold" onClick={() => generateReport('threat')} disabled={generating} title="Threat & exposure assessment: digital exposure, reputation and people-threat led">
                                {generating ? 'Generating…' : '🛡 Threat PDF'}
                            </button>
                            {biz.company_number && (
                                <button className="btn-ghost" onClick={refreshCH} disabled={refreshing}>
                                    {refreshing ? 'Refreshing…' : 'Refresh from CH'}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Monitoring subscription */}
                    <SubscriptionPanel token={token} targetKind="business" targetId={businessId} />

                    {/* Analyst-reorderable panel stack — drag any panel by its grip. */}
                    <ReorderableChildren storageKey="veil_panelorder_business_detail" ids={[
                        score && 'risk_score', 'synthesis', 'signoff', 'snapshot', 'digital_exposure',
                        'ownership', 'ubo', 'related', 'entity_graph', 'filing_history',
                        'linked_subjects', 'hostile_watch', biz.company_number && 'officers',
                    ]}>

                    {/* Risk score panel */}
                    {score && (
                        <div className="panel mb-6">
                            <div className="panel-header flex justify-between items-center">
                                <div className="font-display text-xl gold">Risk Picture (Auto-Scored)<HelpTip text={PANEL_HELP.risk_picture} /></div>
                                <div className="text-xs font-mono text-muted">Live · not signed off</div>
                            </div>
                            <div className="px-6 py-5">
                                <div className="flex items-center gap-8 mb-5">
                                    <div>
                                        <div className="text-xs font-mono uppercase tracking-widest text-muted">Composite</div>
                                        <div className="font-display text-4xl gold">{Number(score.composite_score).toFixed(1)}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs font-mono uppercase tracking-widest text-muted">Band</div>
                                        <div className="font-display text-2xl" style={{
                                            color: score.risk_band === 'critical' ? 'var(--red)' :
                                                   score.risk_band === 'high' ? 'var(--amber)' :
                                                   score.risk_band === 'moderate' ? 'var(--gold)' : 'var(--green)'
                                        }}>{score.risk_band.toUpperCase()}</div>
                                    </div>
                                </div>
                                <div className="grid grid-cols-4 gap-4">
                                    {[
                                        { label: 'THREAT (35%)', score: score.threat_score, drivers: score.threat_drivers },
                                        { label: 'VULNERABILITY (25%)', score: score.vulnerability_score, drivers: score.vulnerability_drivers },
                                        { label: 'IMPACT (25%)', score: score.impact_score, drivers: score.impact_drivers },
                                        { label: 'DISINFORMATION (15%)', score: score.disinformation_score, drivers: score.disinformation_drivers },
                                    ].map(layer => (
                                        <div key={layer.label}>
                                            <div className="text-xs font-mono uppercase tracking-widest text-muted">{layer.label}</div>
                                            <div className="font-display text-xl">{Number(layer.score).toFixed(1)}</div>
                                            <div className="text-xs text-muted mt-2">
                                                {layer.drivers.slice(0, 2).map((d, i) => (
                                                    <div key={i}>• {d}</div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Synthesis verdict — the fused Proceed/Caution/Do-not call */}
                    <BusinessSynthesisPanel token={token} businessId={businessId} />

                    {/* Analyst assessment + sign-off — drives the report's DRAFT/SIGNED state */}
                    <BusinessSignoffPanel token={token} businessId={businessId} score={score} onSigned={reload} />

                    {/* Snapshot panel */}
                    <div className="panel mb-6">
                        <div className="panel-header">
                            <div className="font-display text-xl gold">Snapshot</div>
                            {refreshedAge !== null && (
                                <div className="text-xs font-mono text-muted">
                                    CH refreshed {refreshedAge === 0 ? 'today' : `${refreshedAge} day(s) ago`}
                                </div>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-6 px-6 py-5">
                            <div className="space-y-3 text-sm">
                                <div>
                                    <div className="text-xs text-muted uppercase tracking-widest">Status</div>
                                    <div>{biz.company_status || '—'}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-muted uppercase tracking-widest">Type</div>
                                    <div>{biz.company_type || '—'}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-muted uppercase tracking-widest">Incorporated</div>
                                    <div>{biz.incorporated_at || '—'}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-muted uppercase tracking-widest">Registered Address</div>
                                    <div>{biz.registered_address || '—'}</div>
                                </div>
                            </div>
                            <div className="space-y-3 text-sm">
                                <div>
                                    <div className="text-xs text-muted uppercase tracking-widest">SIC Codes</div>
                                    <div>{biz.sic_codes.length > 0 ? biz.sic_codes.join(', ') : '—'}</div>
                                </div>
                                <div>
                                    <div className="text-xs text-muted uppercase tracking-widest">Accounts</div>
                                    <div>
                                        {biz.accounts_last_made_up_at ? `Last: ${biz.accounts_last_made_up_at}` : 'Not filed'}
                                        {biz.accounts_next_due_at && <span> · Next due: {biz.accounts_next_due_at}</span>}
                                        {biz.accounts_overdue && <span className="ml-2" style={{color:'var(--amber)'}}>OVERDUE</span>}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-xs text-muted uppercase tracking-widest">Confirmation Statement</div>
                                    <div>
                                        {biz.confirmation_next_due_at ? `Next due: ${biz.confirmation_next_due_at}` : '—'}
                                        {biz.confirmation_overdue && <span className="ml-2" style={{color:'var(--amber)'}}>OVERDUE</span>}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Digital exposure (0049 engine, now surfaced) */}
                    <BusinessDigitalExposure token={token} businessId={businessId} biz={biz} reload={reload} />

                    {/* Ownership & charges — PSCs + secured debt */}
                    <BusinessOwnershipControl token={token} businessId={businessId} hasCompany={!!biz.company_number} />

                    {/* Ultimate beneficial ownership — effective % through the chain */}
                    <BusinessUltimateOwners token={token} businessId={businessId} hasCompany={!!biz.company_number} />

                    {/* Director network — related companies via shared directors */}
                    <BusinessRelatedCompanies token={token} businessId={businessId} hasCompany={!!biz.company_number} />

                    <EntityGraphPanel token={token} businessId={businessId} hasCompany={!!biz.company_number} />

                    {/* Corporate events — recent CH filing history */}
                    <BusinessFilingHistory token={token} businessId={businessId} hasCompany={!!biz.company_number} />

                    {/* Linked Subjects */}
                    <div className="panel mb-6">
                        <div className="panel-header">
                            <div className="font-display text-xl gold">Linked Subjects ({links.length})</div>
                        </div>
                        {links.length === 0 ? (
                            <div className="px-6 py-5 text-sm text-muted">
                                No subjects linked yet. Use "Officers from Companies House" below to find directors and convert them to Subjects.
                            </div>
                        ) : (
                            <div>
                                {links.map(l => {
                                    const bandColour = b => b === 'critical' ? 'var(--red)' :
                                                            b === 'high'     ? 'var(--amber)' :
                                                            b === 'moderate' ? 'var(--gold)' :
                                                            'var(--green)';
                                    return (
                                        <div key={l.id} className="table-row px-6 py-3 flex justify-between items-center gap-4">
                                            <div className="flex-1 min-w-0 cursor-pointer" onClick={() => { setSelectedSubject(l.subject_id); setView('subject-detail'); }}>
                                                <div>{l.subject_display_name || l.subject_full_name}</div>
                                                <div className="text-xs text-muted font-mono">
                                                    {l.link_type.replace(/_/g, ' ')}
                                                    {l.appointed_at && ` · appointed ${l.appointed_at}`}
                                                    {l.resigned_at && ` · resigned ${l.resigned_at}`}
                                                    {!l.is_current && ' · former'}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3" style={{minWidth: '260px', justifyContent: 'flex-end'}}>
                                                {l.subject_hostile_incidents_active > 0 && (
                                                    <span className="severity-pill band-moderate">
                                                        {l.subject_hostile_incidents_active} active incident(s)
                                                    </span>
                                                )}
                                                {l.subject_risk_band && (
                                                    <span className="font-mono text-xs uppercase tracking-widest"
                                                          style={{color: bandColour(l.subject_risk_band)}}>
                                                        {l.subject_risk_band}
                                                        {l.subject_composite_score != null && ` · ${l.subject_composite_score.toFixed(1)}`}
                                                    </span>
                                                )}
                                                <button className="text-xs text-muted hover:text-[var(--red)] uppercase tracking-widest"
                                                        onClick={(e) => { e.stopPropagation(); removeLink(l); }}>
                                                    Unlink
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Hostile Mention Watch */}
                    <BusinessHostileWatch token={token} businessId={businessId} />

                    {/* Officers from CH */}
                    {biz.company_number && (
                        <div className="panel">
                            <div className="panel-header flex justify-between items-center">
                                <div className="font-display text-xl gold">Officers from Companies House<HelpTip text={PANEL_HELP.officers} /></div>
                                <div className="flex gap-2">
                                    {unlinkedOfficers.length > 0 && (
                                        <button className="btn-gold" style={{fontSize:'11px', padding:'6px 12px'}}
                                            onClick={() => openDialog('batch-link-officers', {
                                                businessId, officers: unlinkedOfficers,
                                                onSuccess: () => { loadOfficers(); reload(); },
                                            })}>
                                            Link and sweep all ({unlinkedOfficers.length})
                                        </button>
                                    )}
                                    <button className="btn-ghost" onClick={loadOfficers} disabled={loadingOfficers}>
                                        {loadingOfficers ? 'Loading…' : (officers ? 'Refresh' : 'Load officers')}
                                    </button>
                                </div>
                            </div>
                            {officersError && <div className="px-6 py-3 text-sm" style={{color:'var(--red)'}}>{officersError}</div>}
                            {suggestedOfficers.length > 0 && (
                                <div className="px-6 py-4" style={{borderBottom: '1px solid var(--border)', background: 'rgba(197, 165, 92, 0.06)'}}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <span className="font-mono text-xs uppercase tracking-widest" style={{color: 'var(--gold)'}}>
                                            Suggested links ({suggestedOfficers.length})
                                        </span>
                                        <HelpTip text={PANEL_HELP.officer_suggested_links} />
                                    </div>
                                    <div className="text-xs text-muted mb-3">
                                        Nothing below is linked until you click Confirm. Dismissing just hides the
                                        suggestion on this screen — it doesn't change any record.
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        {suggestedOfficers.map((o, idx) => (
                                            <div key={(o.officer_id || 'no-id') + '_sugg_' + idx}
                                                 className="flex justify-between items-center gap-3 text-sm px-3 py-2"
                                                 style={{border: '1px solid var(--border)', borderRadius: '4px'}}>
                                                <div className="flex-1 min-w-0">
                                                    Likely the same person as <strong>{o.suggested_subject_name}</strong>
                                                    {' '}— {o.name} is a {o.role || 'officer'} of this company, and the
                                                    name closely matches an existing Subject already held
                                                    (confidence {(o.match_confidence * 100).toFixed(0)}%, name-match basis).
                                                </div>
                                                <div className="flex gap-2 shrink-0">
                                                    <button className="btn-gold" style={{fontSize:'11px', padding:'4px 10px'}}
                                                        disabled={confirmingSuggestion === (o.officer_id || o.name)}
                                                        onClick={() => confirmSuggestion(o)}>
                                                        {confirmingSuggestion === (o.officer_id || o.name) ? 'Linking…' : 'Confirm link'}
                                                    </button>
                                                    <button className="btn-ghost" style={{fontSize:'11px', padding:'4px 10px'}}
                                                        onClick={() => dismissSuggestion(o)}>
                                                        Dismiss
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {officers && officers.length === 0 && (
                                <div className="px-6 py-5 text-sm text-muted">No officers returned.</div>
                            )}
                            {officers && officers.length > 0 && (
                                <div>
                                    {officers.map((o, idx) => (
                                        <div key={(o.officer_id || 'no-id') + '_' + idx} className="table-row px-6 py-3 flex justify-between items-start gap-4">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm">{o.name}</div>
                                                <div className="text-xs text-muted font-mono mt-1">
                                                    {o.role}
                                                    {o.appointed_on && ` · appointed ${o.appointed_on}`}
                                                    {o.resigned_on && ` · resigned ${o.resigned_on}`}
                                                    {o.dob_month && o.dob_year && ` · born ${o.dob_month}/${o.dob_year}`}
                                                </div>
                                                {o.nationality && (
                                                    <div className="text-xs text-muted font-mono">
                                                        {o.nationality}
                                                        {o.occupation && ` · ${o.occupation}`}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex flex-col gap-1 items-end" style={{minWidth: '180px'}}>
                                                {o.already_linked ? (
                                                    <span className="text-xs font-mono" style={{color: 'var(--green)'}}>✓ Linked</span>
                                                ) : o.existing_subject_id ? (
                                                    <button className="btn-ghost" style={{fontSize:'11px', padding:'4px 10px'}}
                                                        onClick={() => linkExistingSubject(o, o.existing_subject_id)}>
                                                        + Link existing Subject
                                                    </button>
                                                ) : (
                                                    <button className="btn-ghost" style={{fontSize:'11px', padding:'4px 10px'}}
                                                        onClick={() => openDialog('link-officer-to-subject', { businessId, officer: o, onSuccess: () => { loadOfficers(); reload(); } })}>
                                                        + Create Subject + Link
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    </ReorderableChildren>
                </div>
            );
        }

export { BusinessDetailView };
